// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartLineContainer, { CartLineContainerStyles } from "@adl/App/Components/Cart/CartContainerLayout";
import CalculateMargin from "@adl/App/Components/CustomQuote/CalculateMargin";
import ProductLineRemoveButton from "@adl/App/Components/Product/ProductLineRemoveButton";
import getQuoteDetails from "@adl/App/Handlers/CustomQuote/GetCustomQuote";
import updateQuantity from "@adl/App/Handlers/CustomQuote/UpdateQuantity";
import updateQuoteLine from "@adl/App/Handlers/CustomQuote/UpdateQuoteLine";
import { CustomQuoteLinesModel, CustomQuoteModel, CustomQuoteProductInfo } from "@adl/App/Services/CustomQuotesService";
import productDescriptionStyles from "@adl/App/SharedStyle/ProductDescriptionTextStyles";
import { CustomAppState } from "@adl/App/Store/Reducers";
import ProductPartNumbers from "@adl/Overrides/Components/ProductPartNumbers";
import ProductQuantityOrdered from "@adl/Overrides/Components/ProductQuantityOrdered";
import { xScreen } from "@adl/XDL/Utilities";
import { iconLinkCommonStyles } from "@adl/XDL/Utilities/links";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import {
    productAvailabilityStyles,
    ProductAvailabilityStyles,
} from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    CustomQuote: CustomQuoteModel;
    CustomQuoteLine: CustomQuoteLinesModel;
    productInfo: CustomQuoteProductInfo;
    onDeleteClick: (CustomQuoteLine: CustomQuoteLinesModel) => void;
    onEditNotesClick: (CustomQuoteLine: CustomQuoteLinesModel) => void;
    showImageBadges: boolean;
    maximumNumberOfImageBadges: number;
    showTextBadges: boolean;
    maximumNumberOfTextBadges: number;
}

const mapStateToProps = (state: CustomAppState) => ({
    settingsCollection: getSettingsCollection(state),
    sortBy: state.xMyCustomQuoteDetails.sortBy,
    editingSortOrder: state.xMyCustomQuoteDetails.editingSortOrder,
});

const mapDispatchToProps = {
    updateQuantity,
    updateQuoteLine,
    getQuoteDetails,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyCustomQuotesDetailsProductListStyles {
    productImageStyles?: ProductImageStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productPriceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    updateQuantityLink?: LinkPresentationProps;
    editNotesLinkWrapper?: InjectableCss;
    editNotesLink?: LinkPresentationProps;
    textBadgeStyles?: ProductTextBadgesStyles;
    imageBadgeStyles?: ProductImageBadgesStyles;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productQuantityOrderedStyles?: TextFieldProps;
    lineInnerContainer?: CartLineContainerStyles;
    productInfoContainer?: InjectableCss;
    productPartNumbersGridItem?: InjectableCss;
    removeButton?: any;
    quotePriceInput?: InjectableCss;
    customPriceTextField?: TextFieldProps;
    totalWrapper?: InjectableCss;
    pricesWrapper?: InjectableCss;
    totalPriceStyles?: TypographyProps;
    quotePriceWrapper?: InjectableCss;
    quotePriceStyles?: TypographyProps;
    uomQuoteStyles?: TypographyProps;
    sortOrderGridItem?: InjectableCss;
    sortOrderTextField?: TextFieldProps;
    customQuoteLabelStyle?: TypographyProps;
}

export const productCustomQuoteLineStyles: MyCustomQuotesDetailsProductListStyles = {
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                width: 75px;
                height: 75px;
                margin-bottom: 8px;
                ${xScreen("lte", "sm")} {
                    img {
                        margin: auto;
                    }
                }
            `,
        },
        img: {
            css: css`
                max-width: 100%;
                max-height: 100%;
            `,
        },
    },
    productDescriptionStyles: {
        ...productDescriptionStyles("small", {
            productDetailLink: {
                css: css`
                    margin-bottom: 12px;
                `,
            },
            productDetailDescription: {
                css: css`
                    margin-bottom: 12px;
                `,
            },
        }),
    },
    productPriceStyles: {
        wrapper: {
            css: css`
                ${xScreen("lte", "xs")} {
                    align-items: flex-start;
                    white-space: nowrap;
                }
            `,
        },
    },
    updateQuantityLink: {
        typographyProps: {
            size: 11,
            weight: "bold",
            css: css`
                line-height: 15px;
            `,
        },
        css: css`
            position: absolute;
            bottom: -26px;
            ${xScreen("lte", "md")} {
                bottom: -16px;
            }
        `,
    },
    editNotesLinkWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            ${xScreen("lte", "md")} {
                width: 65% !important;
                margin-left: 10px;
                margin-bottom: 0;
            }
            ${xScreen("lte", "xs")} {
                width: 100% !important;
                margin-left: 0;
            }
        `,
    },
    editNotesLink: {
        ...iconLinkCommonStyles(File),
        css: css`
            white-space: nowrap;
        `,
    },
    textBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
    },
    imageBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
        imageItem: {
            css: css`
                margin: 5px 0;
            `,
        },
    },
    productAvailabilityStyles: {
        container: {
            css: css`
                ${productAvailabilityStyles?.container?.css}
                width: 50%;
                justify-content: flex-start;
                flex-grow: 1;
                white-space: nowrap;
                ${xScreen("lte", "sm")} {
                    flex-grow: initial;
                }
                button[data-test-selector="availabilityByWarehouseLink"] {
                    margin-top: 6px;
                }
            `,
        },
    },
    lineInnerContainer: {
        container: {
            css: css`
                padding: 20px 0 18px;
            `,
        },
        qtyPriceSection: {
            css: css`
                ${xScreen("lte", "xs")} {
                    flex-wrap: wrap;
                    margin-bottom: 0;
                }
            `,
        },
        qty: {
            css: css`
                position: relative;
                > div:first-child {
                    display: flex;
                    flex-direction: column;
                }
                button[data-test-selector="addToCart"] {
                    margin-left: 12px;
                    flex-grow: 1;
                    ${xScreen("gt", "md")} {
                        display: none;
                    }
                }
                ${xScreen("lte", "md")} {
                    flex-wrap: wrap;
                    max-width: 50%;
                }
                ${xScreen("lte", "sm")} {
                    max-width: 100%;
                }
            `,
        },
        price: {
            css: css`
                position: relative;
                align-items: flex-start;
                ${xScreen("lte", "md")} {
                    align-items: center;
                }
            `,
        },
        total: {
            css: css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                flex-wrap: wrap;
                > button {
                    width: 100%;
                    margin-bottom: 16px;
                }
                > div {
                    margin-bottom: 16px;
                }
                ${xScreen("lte", "sm")} {
                    > div {
                        width: 100%;
                    }
                    min-width: 100%;
                }
                ${xScreen("lte", "xs")} {
                    flex: 100%;
                    margin-top: 22px;
                    flex-wrap: wrap;
                }
            `,
        },
    },
    productInfoContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
    productPartNumbersGridItem: {
        css: css`
            width: 50%;
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
        `,
    },
    removeButton: {
        wrapper: {
            css: css`
                &&& {
                    margin-bottom: 15px;
                }
                ${xScreen("lte", "md")} {
                    &&& {
                        margin-bottom: 0;
                    }
                }
            `,
        },
        innerWrapper: {
            css: css`
                ${xScreen("lte", "xs")} {
                    position: static;
                }
                ${xScreen("gte", "lg")} {
                    bottom: -20px;
                }
            `,
        },
    },
    quotePriceInput: {
        css: css`
            display: flex;
            margin-top: 20px;
            align-items: flex-end;
            position: relative;
            width: fit-content;
            label {
                white-space: nowrap;
                z-index: 3;
            }
            .dollar-sign {
                position: absolute;
                left: 5px;
                bottom: 7px;
            }
            .uom-style {
                position: absolute;
                right: 5px;
                bottom: 7px;
                font-size: 12px;
            }
            ${xScreen("lte", "md")} {
                margin-top: 10px;
            }
        `,
    },
    customPriceTextField: {
        sizeVariant: "small",
        labelProps: {
            css: css`
                white-space: nowrap;
            `,
        },
        cssOverrides: {
            inputSelect: css`
                width: 102px;
                height: 35px;
                padding: 0 25px 0 15px;
                font-size: 14px;
                &:focus {
                    padding: 0 25px 0 15px;
                }
            `,
            formField: css`
                width: auto;
            `,
        },
    },
    totalWrapper: {
        css: css`
            font-weight: bold;
            font-size: 16px;
            display: flex;
            justify-content: space-between;
            width: 100%;
            ${xScreen("lte", "md")} {
                align-items: center;
                width: fit-content;
                margin-bottom: 10px;
            }
        `,
    },
    pricesWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            span[data-test-selector="notify-warning"] {
                margin-bottom: 0 !important;
            }
        `,
    },
    totalPriceStyles: {
        ellipsis: true,
        css: css`
            display: inline-block;
        `,
    },
    quotePriceStyles: {
        weight: 700,
        size: 16,
    },
    uomQuoteStyles: {
        size: 12,
    },
    sortOrderGridItem: {
        css: css`
            &&& {
                width: 100%;
            }
            > div {
                flex-direction: column;
                label {
                    text-align: left;
                    padding: 0;
                }
                div {
                    width: 100%;
                }
            }
        `,
    },
    sortOrderTextField: {
        labelPosition: "left",
        cssOverrides: {
            formInputWrapper: css`
                width: 60px;

                ${xScreen("lte", "sm")} {
                    margin-bottom: 16px;
                }
            `,
        },
        labelProps: {
            css: css`
                width: auto;
            `,
        },
    },
    customQuoteLabelStyle: {
        size: 12,
        weight: 600,
        color: "danger",
        css: css`
            display: inline-block;
            padding-top: 10px;
        `,
    },
};

const styles = productCustomQuoteLineStyles;

const MyCustomQuotesDetailsProductListLine = ({
    CustomQuote,
    CustomQuoteLine,
    productInfo,
    settingsCollection,
    onDeleteClick,
    onEditNotesClick,
    updateQuantity,
    showImageBadges,
    showTextBadges,
    maximumNumberOfImageBadges,
    maximumNumberOfTextBadges,
    updateQuoteLine,
    getQuoteDetails,
    sortBy,
    editingSortOrder,
}: Props) => {
    const product = CustomQuoteLine.productResult;
    const toasterContext = useContext(ToasterContext);
    const [quotePrice, setQuotePrice] = useState(CustomQuoteLine?.extendedPrice?.toFixed(2) || "0.00");
    const [totalPrice, setTotalPrice] = useState(
        (CustomQuoteLine?.extendedPrice * CustomQuoteLine?.qty).toFixed(2) || "0.00",
    );

    const { productSettings } = settingsCollection;
    const canAddToCart = !CustomQuote.allowRedirectToAssociatedQuote && !CustomQuote.isCameFromNormalQuote;

    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: (qtyOrdered: any) => {
            updateQuantity({ lineId: CustomQuoteLine.lineId, qtyOrdered } as any);
        },
    } as any;

    const onUpdateQuantityClick = () => {
        updateQuoteLine({
            updateNote: false,
            updateQty: true,
            updatePrice: false,
            linesModel: [
                {
                    lineId: productInfo?.lineId,
                    qty: productInfo?.qtyOrdered,
                },
            ],
            onSuccess(result: any) {
                getQuoteDetails({
                    quoteId: CustomQuote.id,
                    skipPricingAndInventory: true,
                    sortBy,
                });
                toasterContext.addToast({ body: translate("Qty Updated"), messageType: "success" });
            },
            onError(error: any) {
                toasterContext.addToast({ body: error, messageType: "danger" });
            },
        });
    };

    const onQuotePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.value) {
            setQuotePrice("");
            return;
        }
        setQuotePrice(event.target.value);
    };

    const onQuotePriceBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const price = parseFloat(event.target.value);
        if (Number.isNaN(price)) {
            setQuotePrice(CustomQuoteLine.extendedPrice.toFixed(2));
            return;
        }
        setQuotePrice(price.toString());
        if (CustomQuoteLine.extendedPrice !== null && event.target.value === CustomQuoteLine.extendedPrice.toString()) {
            return;
        }
        updateQuoteLine({
            updateNote: false,
            updateQty: false,
            updatePrice: true,
            linesModel: [
                {
                    lineId: productInfo?.lineId,
                    price,
                },
            ],
            onSuccess(result: any) {
                getQuoteDetails({
                    quoteId: CustomQuote.id,
                    skipPricingAndInventory: true,
                    sortBy,
                });
                toasterContext.addToast({ body: translate("Price Updated"), messageType: "success" });
            },
            onError(error) {
                toasterContext.addToast({ body: error, messageType: "danger" });
            },
        });
    };

    const sortOrderBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        updateSortOrder(CustomQuoteLine, event.target.value);
    };

    const sortOrderKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const updateSortOrder = (customQuoteLine: CustomQuoteLinesModel, value: number | string) => {
        const sortOrder = parseInt(value.toString() || "1", 10);
        if (customQuoteLine.sortOrder === sortOrder) {
            return;
        }

        updateQuoteLine({
            updateNote: false,
            updateQty: false,
            updatePrice: false,
            updateSort: true,
            linesModel: [
                {
                    lineId: productInfo?.lineId,
                    sortOrder,
                },
            ],
            onSuccess(result: any) {
                getQuoteDetails({
                    quoteId: CustomQuote.id,
                    skipPricingAndInventory: false,
                    sortBy,
                });
                toasterContext.addToast({ body: translate("Sort Order Updated"), messageType: "success" });
            },
            onError(error) {
                toasterContext.addToast({ body: error, messageType: "danger" });
            },
        });
    };

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <ProductContext.Provider value={productContext}>
            <CartLineContainer
                data-test-selector={`${CustomQuoteLine.lineId}_${CustomQuoteLine.unitOfMeasureDisplay}`}
                extendedStyles={styles.lineInnerContainer}
            >
                <CartLineContainer.Image>
                    <ProductImage
                        product={productContext}
                        extendedStyles={styles.productImageStyles}
                        {...{ transformations: { width: 75 } }}
                    />
                </CartLineContainer.Image>
                <CartLineContainer.Info>
                    <StyledWrapper {...styles.productInfoContainer}>
                        <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                        <StyledWrapper {...styles.productPartNumbersGridItem}>
                            <ProductPartNumbers
                                productNumber={product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                showBrand={true}
                                brand={product.brand}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.productPartNumbersGridItem}>
                            {productSettings.showInventoryAvailability && (
                                <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                            )}
                            {productContext.productInfo.inventory?.additionalResults?.customQuoteLineLabel &&
                                productContext.productInfo.inventory?.additionalResults?.customQuoteLineLabel !==
                                    "" && (
                                    <Typography {...styles.customQuoteLabelStyle}>
                                        {translate(
                                            productContext.productInfo.inventory?.additionalResults
                                                ?.customQuoteLineLabel,
                                        )}
                                    </Typography>
                                )}
                        </StyledWrapper>
                    </StyledWrapper>
                </CartLineContainer.Info>
                <CartLineContainer.Qty>
                    <StyledWrapper>
                        <ProductQuantityOrdered
                            extendedStyles={styles.productQuantityOrderedStyles}
                            labelOverride=""
                            showActions={true}
                        />
                        {productInfo.qtyOrdered &&
                            productInfo.qtyOrdered > 0 &&
                            CustomQuoteLine.qty !== productInfo.qtyOrdered && (
                                <Link {...styles.updateQuantityLink} onClick={() => onUpdateQuantityClick()}>
                                    {translate("Update QTY")}
                                </Link>
                            )}
                    </StyledWrapper>
                    {showTextBadges && textBadges && (
                        <ProductTextBadges
                            badgeList={textBadges}
                            maxBadges={maximumNumberOfTextBadges ?? 4}
                            extendedStyles={styles.textBadgeStyles}
                        />
                    )}
                    {showImageBadges && imageBadges && (
                        <ProductImageBadges
                            badgeList={imageBadges}
                            maxBadges={maximumNumberOfImageBadges ?? 4}
                            extendedStyles={styles.imageBadgeStyles}
                            numberOfColumns={4}
                        />
                    )}
                </CartLineContainer.Qty>
                <CartLineContainer.Price>
                    <StyledWrapper {...styles.pricesWrapper}>
                        {!canAddToCart && CustomQuoteLine.quotePrice ? (
                            <StyledWrapper {...styles.quotePriceWrapper}>
                                <Typography {...styles.quotePriceStyles}>
                                    ${CustomQuoteLine.quotePrice.toFixed(2)}
                                </Typography>
                                <Typography {...styles.uomQuoteStyles}>
                                    &nbsp;/&nbsp;{CustomQuoteLine.unitOfMeasureDisplay.toUpperCase()}
                                </Typography>
                            </StyledWrapper>
                        ) : (
                            <ProductPrice
                                product={productContext}
                                showSavings={true}
                                showSavingsAmount={productSettings.showSavingsAmount}
                                showSavingsPercent={productSettings.showSavingsPercent}
                                extendedStyles={styles.productPriceStyles}
                                {...{ showRetailPrice: true }}
                            />
                        )}

                        <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                        <StyledWrapper {...styles.quotePriceInput}>
                            <label className="dollar-sign">{translate("$")}</label>
                            <TextField
                                type="text"
                                value={quotePrice}
                                onChange={onQuotePriceChange}
                                onBlur={onQuotePriceBlur}
                                label={translate("Quote Price")}
                                {...styles.customPriceTextField}
                            />
                            <label className="uom-style">
                                &nbsp;/&nbsp;{product.unitOfMeasures?.[0].unitOfMeasureDisplay.toUpperCase()}
                            </label>
                        </StyledWrapper>
                    </StyledWrapper>
                </CartLineContainer.Price>
                <CartLineContainer.Total>
                    <StyledWrapper {...styles.totalWrapper}>
                        <Typography {...styles.totalPriceStyles}>
                            {translate("Total")}: ${totalPrice}
                        </Typography>
                        <ProductLineRemoveButton
                            onRemove={() => onDeleteClick(CustomQuoteLine)}
                            extendedStyles={styles.removeButton}
                        />
                    </StyledWrapper>
                    {editingSortOrder && (
                        <StyledWrapper {...styles.sortOrderGridItem}>
                            <TextField
                                {...styles.sortOrderTextField}
                                label={translate("Sort Order")}
                                type="number"
                                min={1}
                                defaultValue={CustomQuoteLine.sortOrder}
                                onBlur={sortOrderBlurHandler}
                                onKeyPress={sortOrderKeyPressHandler}
                            />
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.editNotesLinkWrapper}>
                        <CalculateMargin
                            productsInfos={[productInfo]}
                            quoteId={CustomQuote.id}
                            quoteLines={[CustomQuoteLine]}
                            isApplyAllMargins={false}
                        />
                        <Link
                            {...styles.editNotesLink}
                            onClick={() => onEditNotesClick(CustomQuoteLine)}
                            data-test-selector="myCustomQuoteDetails_addOrEditNote"
                        >
                            {translate(`${CustomQuoteLine.notes ? "Edit" : "Add"} Notes`)}
                        </Link>
                    </StyledWrapper>
                </CartLineContainer.Total>
            </CartLineContainer>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MyCustomQuotesDetailsProductListLine);
